import { useState, useEffect } from "react";
import { useAuth, isDevMode } from "@/lib/auth";
import { getApiBaseUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LogIn, Loader2, ChevronDown, ChevronRight } from "lucide-react";

type DevUser = {
  nin: string;
  name: string;
  age: number;
  gender: string;
  roles: string[];
  hasLabData: boolean;
};

export function LoginPage() {
  const { login, loginWithBankID, isLoggingIn, loginError } = useAuth();
  const [idToken, setIdToken] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);
  const [devUsers, setDevUsers] = useState<DevUser[]>([]);
  const isDev = isDevMode();

  useEffect(() => {
    if (!isDev) return;
    fetch(`${getApiBaseUrl()}/api/auth/dev-users`)
      .then((r) => r.json())
      .then((data) =>
        setDevUsers(
          (data.users ?? []).filter((u: DevUser) => ["admin", "doctor", "bht"].some((r) => u.roles.includes(r)))
        )
      )
      .catch((error) => {
        console.error("Failed to fetch dev users:", error);
      });
  }, [isDev]);

  const handleDevSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idToken.trim()) return;
    try {
      await login(idToken.trim());
    } catch {
      // error is set in auth context
    }
  };

  const handleUserClick = async (nin: string) => {
    try {
      await login(nin);
    } catch {
      // error is set in auth context
    }
  };

  return (
    <div className="min-h-screen bg-admin-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Amino Backoffice</CardTitle>
          <p className="text-sm text-admin-text-subtle mt-1">Logg inn for å fortsette</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* BankID login */}
          <Button className="w-full" disabled={isLoggingIn} onClick={() => loginWithBankID()}>
            {isLoggingIn ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            {isLoggingIn ? "Logger inn..." : "Logg inn med BankID"}
          </Button>

          {loginError && (
            <div className="rounded-md bg-admin-danger-500/10 border border-admin-danger-500/20 p-3">
              <p className="text-sm text-admin-danger-400">{loginError}</p>
            </div>
          )}

          {/* Dev mode: test user picker */}
          {isDev && (
            <div className="border-t border-admin-card-border pt-3 space-y-3">
              <p className="text-xs text-admin-text-subtle">Dev: Testbrukere</p>

              {devUsers.length > 0 && (
                <div className="grid gap-2">
                  {devUsers.map((user) => (
                    <button
                      key={user.nin}
                      className="flex items-center justify-between w-full rounded-lg border border-admin-card-border p-3 text-left hover:bg-admin-card-hover transition-colors disabled:opacity-50"
                      onClick={() => handleUserClick(user.nin)}
                      disabled={isLoggingIn}
                    >
                      <div>
                        <span className="text-sm font-medium">{user.name}</span>
                        <span className="text-xs text-admin-text-subtle ml-2">
                          {user.age} år · {user.gender === "male" ? "M" : "K"}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {user.roles.map((role) => (
                          <Badge key={role} variant="secondary" className="text-[10px]">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Collapsible manual input */}
              <button
                className="flex items-center gap-1 text-xs text-admin-text-subtle hover:text-admin-text-secondary"
                onClick={() => setShowManualInput(!showManualInput)}
              >
                {showManualInput ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Manuell ID Token / NIN
              </button>
              {showManualInput && (
                <form onSubmit={handleDevSubmit} className="space-y-2">
                  <Input
                    value={idToken}
                    onChange={(e) => setIdToken(e.target.value)}
                    placeholder="ID token eller fødselsnummer (11 siffer)..."
                    autoFocus
                    disabled={isLoggingIn}
                  />
                  <Button type="submit" variant="outline" className="w-full" disabled={isLoggingIn || !idToken.trim()}>
                    Dev-innlogging
                  </Button>
                </form>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
